'use client';

import { ReactComponent as AwardIcon } from '@assets/icons/award.svg';
import { ReactComponent as DefaultUserAvatar } from '@assets/icons/default-user-avatar.svg';
import { ReactComponent as HashtagIcon } from '@assets/icons/hashtag.svg';
import Image from 'next/image';

import { Box, BoxProps, useBreakpointValue, useTheme } from '@chakra-ui/react';

import { imgLoader } from 'utils';

import { Text } from 'ui';

type AvatarProps = BoxProps & {
  useBadges?: boolean;
  isMember: boolean;
  isVIP: boolean;
  isInfluencer: boolean;
  src: string;
  alt: string;
};

const badgePositions = [
  {
    right: { base: '-10px', md: '-15px', lg: '-35px' },
    top: { base: '10px', lg: '10px' }
  },
  {
    right: { base: '-17px', md: '-35px', lg: '-49px' },
    top: '50%',
    transform: 'translateY(-50%)'
  },
  {
    right: { base: '-10px', md: '-15px', lg: '-35px' },
    bottom: { base: '10px', lg: '10px' }
  }
];

const Avatar = ({
  useBadges = false,
  isMember,
  isVIP,
  isInfluencer,
  src,
  alt,
  ...rest
}: AvatarProps) => {
  const isMobile = useBreakpointValue(
    { base: true, md: false },
    { fallback: 'md' }
  );
  const {
    colors: {
      palette: {
        components: { avatar }
      }
    }
  } = useTheme();

  const badgeStyles = {
    position: 'absolute',
    borderRadius: '50%',
    backgroundColor: avatar.badgeBgColor,
    border: '1px solid',
    borderColor: avatar.badgeBorderColor,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: { base: '23px', md: '29px', lg: '39px' },
    height: { base: '23px', md: '29px', lg: '39px' }
  };

  const badges = [
    {
      id: 1,
      condition: isMember,
      content: (
        <Text
          fontSize={{ base: '15px', lg: '16px' }}
          fontWeight='semibold'
          color={avatar.badgeTextColor}
        >
          M
        </Text>
      )
    },
    {
      id: 2,
      condition: isVIP,
      content: (
        <AwardIcon
          width={isMobile ? '10px' : '16px'}
          height={isMobile ? '15px' : '21px'}
          fill={avatar.badgeTextColor}
        />
      )
    },
    {
      id: 3,
      condition: isInfluencer,
      content: (
        <HashtagIcon
          width={isMobile ? '9px' : '14px'}
          height={isMobile ? '11px' : '16px'}
          fill={avatar.badgeTextColor}
        />
      )
    }
  ].filter((badge) => badge.condition);

  return (
    <Box position='relative'>
      <Box
        {...rest}
        display='flex'
        justifyContent='center'
        alignContent='flex-end'
        overflow='hidden'
        bg='white'
        position='relative'
        sx={{
          image: {
            objectFit: 'cover'
          },
          svg: {
            opacity: 0.5,
            transform: 'scale(1.15)'
          }
        }}
      >
        {src ? (
          <Image
            fill
            priority
            src={src}
            alt={alt}
            sizes='(max-width: 450px) 60px, 140px'
            loader={imgLoader}
            style={{
              objectFit: 'cover',
              objectPosition: 'top center'
            }}
          />
        ) : (
          <DefaultUserAvatar />
        )}
      </Box>

      {useBadges &&
        badges.map((badge, index) => (
          <Box
            key={badge.id}
            sx={{ ...badgeStyles, ...badgePositions[index] }}
            zIndex={5}
          >
            {badge.content}
          </Box>
        ))}
    </Box>
  );
};

export default Avatar;
